import http from '../../http-common'
import { requestMediaAccess, clearMediaAccess, clearAllMediaAccess } from './MediaAccess'
import { mediaResolver, type MediaLease } from './MediaResolver'

const leases = new Map<string, Map<string, MediaLease>>()
export const mediaScope = () => localStorage.getItem('cmpId') || localStorage.getItem('empSlug') || ''
export const mediaOwner = (roomId: string | number) => `${mediaScope()}:${roomId}`

export function metadataFile(file: any) {
  const type = String(file.type || file.file_type || 'application/octet-stream').toLowerCase()
  const ref = file.ref || null
  return {
    ...file,
    ref,
    name: file.name || file.file_name || 'Arquivo',
    type,
    audio: type.startsWith('audio/'),
    size: file.size || file.file_size || 0,
    url: '',
    preview: undefined,
    state: ref && file.state !== 'unavailable' ? 'idle' : 'unavailable',
    extension: type.split(';')[0].split('/')[1] || 'bin',
    progress: undefined,
    needsDownload: false,
    uploaded: false,
    managedMedia: true,
  }
}

export async function resolveChatFile(
  roomId: string | number,
  file: any,
  priority = 1,
  progressive = false,
  refresh = false,
): Promise<any> {
  const metadata = metadataFile(file)
  if (metadata.state === 'unavailable') return metadata
  const owner = mediaOwner(roomId)
  const key = `${mediaScope()}:${file.integration_id || ''}:${file.ref}`
  let owned = leases.get(owner)
  if (!owned) {
    owned = new Map()
    leases.set(owner, owned)
  }
  if (progressive) {
    try {
      const access = await requestMediaAccess(mediaScope(), roomId, file.ref, refresh)
      if (leases.get(owner) !== owned) return metadata
      if (access.state === 'unavailable') return { ...metadata, state: 'unavailable' }
      if (access.playback_url && Date.parse(access.expires_at || '') > Date.now()) {
        return {
          ...metadata,
          url: access.playback_url,
          expires_at: access.expires_at,
          state: 'ready',
          uploaded: true,
          loading: false,
          error: false,
        }
      }
      if (!access.download_url) return { ...metadata, state: 'unavailable' }
    } catch {
      return { ...metadata, state: 'error', error: true }
    }
  }
  let lease = owned.get(key)
  if (!lease) {
    lease = mediaResolver.acquire(
      key,
      async (signal) => {
        const response = await http.get(`/communication/${roomId}/media-download/`, {
          params: { ref: file.ref },
          responseType: 'blob',
          signal,
          timeout: 30_000,
        })
        return response.data
      },
      priority,
    )
    owned.set(key, lease)
  }
  try {
    const url = await lease.promise
    if (leases.get(owner) !== owned) return metadata
    return {
      ...metadata,
      url,
      preview: metadata.type.startsWith('image/') ? url : undefined,
      state: 'ready',
      uploaded: true,
      loading: false,
      error: false,
    }
  } catch (error) {
    owned.delete(key)
    lease.release()
    return { ...metadata, state: 'error', error: true, loading: false }
  }
}

export function releaseChatMedia(roomId: string | number, scope = mediaScope()) {
  const owner = `${scope}:${roomId}`
  const owned = leases.get(owner)
  clearMediaAccess(scope, roomId)
  leases.delete(owner)
  owned?.forEach((lease) => lease.release())
}

export function releaseAllChatMedia() {
  const owners = [...leases.values()]
  leases.clear()
  clearAllMediaAccess()
  owners.forEach((owned) => owned.forEach((lease) => lease.release()))
}
